import React, { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Autocomplete, TextField } from '@mui/material';
import api from 'utils/api';

const AddressSearch = ({
  onSelect,
  onInputChange,
  label,
  value,
  size = 'small',
  margin = 'normal',
  autoComplete = 'off',
  popupZIndex,
}) => {
  const cache = useRef(new Map());
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState(value || '');
  const [debouncedQuery] = useDebounce(query, 250);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const handleChange = (e, val, reason) => {
    if (reason !== 'reset') {
      setQuery(val);
      setError('');
      onInputChange?.(val, reason);
    }
  };

  useEffect(() => {
    let active = true;
    async function getResults() {
      if (!debouncedQuery) {
        setOptions([]);
        setLoading(false);
        return;
      }
      if (cache.current.has(debouncedQuery)) {
        setOptions(cache.current.get(debouncedQuery));
        setLoading(false);
        return;
      }
      setLoading(true);
      setOptions([]);

      try {
        const results = await api.get(`/geo/autocomplete?address=${encodeURIComponent(debouncedQuery)}`);
        if (!Array.isArray(results)) throw new Error('Invalid address suggestions');
        const newOptions = results.map((option) => ({
          id: option.placeId || option.place_id,
          description: option.description,
        }));
        cache.current.set(debouncedQuery, newOptions);
        if (active) setOptions(newOptions);
      } catch (e) {
        if (active) setError('Address suggestions could not be loaded. Edit your search and try again.');
      }
      if (active) setLoading(false);
    }
    getResults();
    return () => { active = false; };
  }, [debouncedQuery]);

  const handleSelect = async (e, val) => {
    if (!val?.id) {
      return;
    }
    if (val.description) {
      setQuery(val.description);
    }
    setError('');
    try {
      const place = await api.get(`/geo/place?placeId=${encodeURIComponent(val.id)}`);
      if (!place) throw new Error('Missing place details');
      onSelect(place);
    } catch (err) {
      setError('Address details could not be loaded. Choose another suggestion or try again.');
    }
  };

  return (
    <Autocomplete
      filterOptions={(x) => x}
      loading={loading}
      inputValue={query}
      onChange={handleSelect}
      onInputChange={handleChange}
      getOptionLabel={(o) => o.description || ''}
      options={options}
      componentsProps={{ popper: { sx: { zIndex: popupZIndex } } }}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          margin={margin}
          InputLabelProps={{
            color: 'primary',
          }}
          variant="outlined"
          error={Boolean(error)}
          helperText={error && <span role="alert">{error}</span>}
          autoComplete={autoComplete}
          inputProps={{
            ...params.inputProps,
            autoComplete,
          }}
        />
      )}
    />
  );
};

export default AddressSearch;
